import React from 'react';
import { AlertCircle, CheckCircle2, Clock } from 'lucide-react';

type OptimizationStatus = 'red' | 'yellow' | 'green' | 'error';

interface OptimizationBadgeProps {
  status: OptimizationStatus;
  className?: string;
}

const statusConfig = {
  red: {
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    icon: AlertCircle,
    label: 'Not optimized',
  },
  yellow: {
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    textColor: 'text-yellow-700',
    icon: Clock,
    label: 'Processing',
  },
  green: {
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    icon: CheckCircle2,
    label: 'Optimized',
  },
  error: {
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    icon: AlertCircle,
    label: 'Error',
  },
};

export function OptimizationBadge({ status, className = '' }: OptimizationBadgeProps) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-sm font-medium ${config.bgColor} ${config.borderColor} ${config.textColor} ${className}`}
    >
      <Icon className="w-4 h-4" />
      <span>{config.label}</span>
    </div>
  );
}

export function OptimizationBadgeSmall({ status, className = '' }: OptimizationBadgeProps) {
  const config = statusConfig[status];

  // Return just the colored dot for compact display
  const dotColors = {
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500',
    error: 'bg-red-500',
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <div className={`w-3 h-3 rounded-full ${dotColors[status]}`} />
      <span className="text-xs text-gray-600">{config.label}</span>
    </div>
  );
}

export function OptimizationChip({ status, className = '' }: OptimizationBadgeProps) {
  const statusChipConfig = {
    red: {
      bgColor: 'bg-red-500',
      textColor: 'text-white',
      label: 'Not optimized',
    },
    yellow: {
      bgColor: 'bg-yellow-500',
      textColor: 'text-white',
      label: 'Processing',
    },
    green: {
      bgColor: 'bg-green-500',
      textColor: 'text-white',
      label: 'Optimized',
    },
    error: {
      bgColor: 'bg-red-500',
      textColor: 'text-white',
      label: 'Error',
    },
  };

  const config = statusChipConfig[status];

  return (
    <div
      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bgColor} ${config.textColor} ${className}`}
    >
      <span>{config.label}</span>
    </div>
  );
}
